// editor-modes2.tsx - studio modes: Logic (rules), HUD Builder, Audio
// Ported from the design source (editor-modes2.jsx).
// Persists through the mode-document store as kinds 'logic', 'hud' and 'audio'
// (see LogicDoc, HudDoc, AudioDoc).
// Exports: LogicRoute, HudRoute, AudioRoute (plus shared M2Label, M2Panel)
import { Fragment, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { AudioEngine } from '../../engine';
import { uploadAudioAsset } from '../assets/audioUpload';
import { useToast } from '../design/toast';
import { useProject } from '../project/ProjectProvider';
import { GAMEDATA_DOC_FALLBACK, type GameDataDoc } from './editor-data-fx';
import { useEditorHost } from './EditorHost';
import { useSharedModeDocument } from './panel-system/sharedModeDocument';
import { createTransientSelectionStore } from './panel-system/transientSelection';
import {
  COMPARISON_LABELS,
  EDITOR_ACTION_KINDS,
  LOGIC_ACTION_LABELS,
  LOGIC_COMPARISONS,
  LOGIC_DOCUMENT_VERSION,
  LOGIC_EVENT_KINDS,
  LOGIC_EVENT_LABELS,
  LOGIC_FACTS,
  LOGIC_FACT_LABELS,
  NUMERIC_FACTS,
  QUEST_STATUSES,
  SERVER_SUPPORTED_ACTION_KINDS,
  defaultAction,
  isActionKindUnsupported,
  type AuthoredLogicRule,
  type LogicAction,
  type LogicActionKind,
  type LogicCondition,
  type LogicDocV2,
  type LogicEventKind,
  type LogicFactName,
} from './logicContract';

/* ── shared bits ──────────────────────────────────────────────────────── */
// Re-exported for this file's own existing consumers (CutsceneTimeline,
// IntroSequenceTimeline, editor-data-fx, editor-model, editor-modes3) - the
// real definitions live in panelPrimitives.tsx, a leaf
// module with no editor-internal imports, since this file, screens-editor.tsx
// and editor-pixel.tsx all need M2Panel/M2Label and also depend on each other
// transitively through EditorHost.tsx.
import { M2Panel, M2Label, M2ListRow, EdSlider } from './panelPrimitives';
export { M2Panel, M2Label };

/* ── Logic: no-code rules (when / if / then) ──────────────────────────── */
type LogicChipKind = 'when' | 'if' | 'then';

export function LogicChip({ kind, children }: { kind: LogicChipKind; children: ReactNode }) {
  const colours: Record<LogicChipKind, [string, string, string]> = {
    when: ['var(--ss-warning)', 'color-mix(in srgb, var(--ss-warning) 12%, transparent)', 'color-mix(in srgb, var(--ss-warning) 30%, transparent)'],
    if: ['var(--ss-accent)', 'var(--ss-accent-soft)', 'var(--ss-accent-border)'],
    then: ['var(--ss-success)', 'color-mix(in srgb, var(--ss-success) 12%, transparent)', 'color-mix(in srgb, var(--ss-success) 30%, transparent)'],
  };
  const [c, bg, b] = colours[kind];
  return (
    <span style={{
      fontWeight: 600, fontSize: 'var(--ss-fs-2xs)', letterSpacing: '0.12em', textTransform: 'uppercase',
      color: c, background: bg, border: '1px solid ' + b, borderRadius: 999, padding: '4px 11px', flexShrink: 0,
    }}>{children}</span>
  );
}

// The authored rules use the v1 server contract (see ./logicContract.ts);
// these starters are valid documents the Logic runtime executes as-is.
export const LOGIC_RULES: AuthoredLogicRule[] = [
  {
    id: 'rule-plant',
    name: 'Round end on plant',
    when: 'trigger-enter',
    if: [{ fact: 'event-trigger', op: 'eq', value: 'trigger_round_end' }],
    then: [{ kind: 'announce', message: 'Device planted. Round ending.' }],
    enabled: true,
  },
  {
    id: 'rule-door',
    name: 'Door opens on switch',
    when: 'trigger-enter',
    if: [{ fact: 'event-trigger', op: 'eq', value: 'switch_door_a' }],
    then: [{ kind: 'emit', event: 'door-open-a' }],
    enabled: true,
  },
  {
    id: 'rule-orb',
    name: 'Orb respawn timer',
    when: 'round-start',
    then: [{ kind: 'set-variable', name: 'orb_respawns', value: 0 }],
    enabled: true,
  },
  {
    id: 'rule-heartbeat',
    name: 'Low health heartbeat',
    when: 'score-change',
    then: [{ kind: 'emit', event: 'heartbeat_check' }],
    enabled: false,
  },
];

/** The 'logic' mode document: a v2 logic document the server can execute. */
export type LogicDoc = LogicDocV2;

export const LOGIC_DOC_FALLBACK: LogicDoc = { version: LOGIC_DOCUMENT_VERSION, rules: LOGIC_RULES };

const logicInputStyle: CSSProperties = { padding: '7px 10px', fontSize: 'var(--ss-fs-sm)', borderRadius: 9 };
const logicSelectStyle: CSSProperties = { ...logicInputStyle, appearance: 'none', width: 'auto', minWidth: 0 };

/** One editable condition row inside the If card. */
function ConditionRow({
  condition,
  itemIds,
  triggerIds,
  onChange,
  onRemove,
}: {
  condition: LogicCondition;
  itemIds: string[];
  triggerIds: string[];
  onChange: (next: LogicCondition) => void;
  onRemove: () => void;
}) {
  const numeric = NUMERIC_FACTS.includes(condition.fact);

  function changeFact(fact: LogicFactName) {
    const next: LogicCondition = { fact, op: 'eq', value: NUMERIC_FACTS.includes(fact) ? 1 : '' };
    if (fact === 'variable') next.name = condition.name ?? 'counter';
    if (fact === 'item-count') next.name = itemIds[0] ?? '';
    if (fact === 'event-trigger') next.value = triggerIds[0] ?? '';
    if (fact === 'event-team') next.value = 'raiders';
    if (fact === 'quest-status') {
      next.name = condition.name ?? 'quest';
      next.value = 'active';
    }
    onChange(next);
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', width: '100%' }}>
      <select className="ss-input" value={condition.fact} onChange={(e) => changeFact(e.target.value as LogicFactName)} style={logicSelectStyle}>
        {LOGIC_FACTS.map((f) => <option key={f} value={f}>{LOGIC_FACT_LABELS[f]}</option>)}
      </select>
      {condition.fact === 'item-count' && (
        <select className="ss-input" value={condition.name ?? ''} onChange={(e) => onChange({ ...condition, name: e.target.value })} style={logicSelectStyle}>
          {itemIds.map((id) => <option key={id} value={id}>{id}</option>)}
          {condition.name && !itemIds.includes(condition.name) && <option value={condition.name}>{condition.name}</option>}
        </select>
      )}
      {condition.fact === 'variable' && (
        <input className="ss-input" value={condition.name ?? ''} placeholder="variable"
          onChange={(e) => onChange({ ...condition, name: e.target.value })} style={{ ...logicInputStyle, width: 110 }} />
      )}
      {condition.fact === 'quest-status' && (
        <input className="ss-input" value={condition.name ?? ''} placeholder="quest-id"
          onChange={(e) => onChange({ ...condition, name: e.target.value })} style={{ ...logicInputStyle, width: 110 }} />
      )}
      <select className="ss-input" value={condition.op}
        onChange={(e) => onChange({ ...condition, op: e.target.value as LogicCondition['op'] })} style={logicSelectStyle}>
        {(numeric ? LOGIC_COMPARISONS : (['eq', 'ne'] as const)).map((op) => (
          <option key={op} value={op}>{COMPARISON_LABELS[op]}</option>
        ))}
      </select>
      {condition.fact === 'event-trigger' ? (
        <select className="ss-input" value={String(condition.value)}
          onChange={(e) => onChange({ ...condition, value: e.target.value })} style={{ ...logicSelectStyle, flex: 1, minWidth: 110 }}>
          {triggerIds.map((id) => <option key={id} value={id}>{id}</option>)}
          {!triggerIds.includes(String(condition.value)) && <option value={String(condition.value)}>{String(condition.value) || '(no zones placed)'}</option>}
        </select>
      ) : condition.fact === 'event-team' ? (
        <select className="ss-input" value={String(condition.value)}
          onChange={(e) => onChange({ ...condition, value: e.target.value })} style={logicSelectStyle}>
          <option value="raiders">raiders</option>
          <option value="wardens">wardens</option>
        </select>
      ) : condition.fact === 'quest-status' ? (
        <select className="ss-input" value={String(condition.value)}
          onChange={(e) => onChange({ ...condition, value: e.target.value })} style={logicSelectStyle}>
          {QUEST_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      ) : numeric ? (
        <input className="ss-input" type="number" value={Number(condition.value)}
          onChange={(e) => onChange({ ...condition, value: +e.target.value || 0 })} style={{ ...logicInputStyle, width: 84 }} />
      ) : (
        <input className="ss-input" value={String(condition.value)}
          onChange={(e) => onChange({ ...condition, value: e.target.value })} style={{ ...logicInputStyle, flex: 1, minWidth: 110 }} />
      )}
      <button className="ss-btn ss-btn--ghost" onClick={onRemove}
        style={{ padding: '4px 9px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7, color: 'var(--ss-danger)' }}>Remove</button>
    </div>
  );
}

/** One editable action row inside the Then card. */
function ActionRow({
  action,
  itemIds,
  first,
  onChange,
  onRemove,
  canRemove,
}: {
  action: LogicAction;
  itemIds: string[];
  first: boolean;
  onChange: (next: LogicAction) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  // The picker only offers currently-enabled kinds (feature-flag gated), but
  // if this row already carries a gated kind (an older rule authored before
  // the gate, or the flag was flipped off after it was saved) it stays in
  // the list so the row still shows and edits correctly rather than
  // silently snapping to a different action.
  const options = EDITOR_ACTION_KINDS.includes(action.kind)
    ? EDITOR_ACTION_KINDS
    : [...EDITOR_ACTION_KINDS, action.kind];
  const unsupported = isActionKindUnsupported(action.kind);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <LogicChip kind="then">{first ? 'Then' : 'And'}</LogicChip>
        <select className="ss-input" value={action.kind}
          onChange={(e) => onChange(defaultAction(e.target.value as LogicActionKind, itemIds))} style={logicSelectStyle}>
          {options.map((k) => <option key={k} value={k}>{LOGIC_ACTION_LABELS[k]}</option>)}
        </select>
        {action.kind === 'announce' && (
          <input className="ss-input" value={action.message} placeholder="Message"
            onChange={(e) => onChange({ ...action, message: e.target.value })} style={{ ...logicInputStyle, flex: 1, minWidth: 140 }} />
        )}
        {(action.kind === 'set-variable' || action.kind === 'add-variable') && (
          <Fragment>
            <input className="ss-input" value={action.name} placeholder="variable"
              onChange={(e) => onChange({ ...action, name: e.target.value })} style={{ ...logicInputStyle, width: 120 }} />
            <input className="ss-input" type="number" value={action.value}
              onChange={(e) => onChange({ ...action, value: +e.target.value || 0 })} style={{ ...logicInputStyle, width: 84 }} />
          </Fragment>
        )}
        {action.kind === 'emit' && (
          <input className="ss-input" value={action.event} placeholder="event-name"
            onChange={(e) => onChange({ ...action, event: e.target.value })} style={{ ...logicInputStyle, flex: 1, minWidth: 140 }} />
        )}
        {(action.kind === 'give-item' || action.kind === 'take-item') && (
          <Fragment>
            <select className="ss-input" value={action.name} onChange={(e) => onChange({ ...action, name: e.target.value })} style={logicSelectStyle}>
              {itemIds.map((id) => <option key={id} value={id}>{id}</option>)}
              {action.name && !itemIds.includes(action.name) && <option value={action.name}>{action.name}</option>}
            </select>
            <input className="ss-input" type="number" min={1} value={action.count}
              onChange={(e) => onChange({ ...action, count: Math.max(1, Math.round(+e.target.value || 1)) })} style={{ ...logicInputStyle, width: 70 }} />
          </Fragment>
        )}
        {(action.kind === 'win-game' || action.kind === 'lose-game') && (
          <input className="ss-input" value={action.team ?? ''} placeholder="team (optional)"
            onChange={(e) => onChange({ ...action, team: e.target.value || undefined })} style={{ ...logicInputStyle, flex: 1, minWidth: 120 }} />
        )}
        {action.kind === 'start-quest' && (
          <Fragment>
            <input className="ss-input" value={action.questId} placeholder="quest-id"
              onChange={(e) => onChange({ ...action, questId: e.target.value })} style={{ ...logicInputStyle, width: 120 }} />
            <input className="ss-input" value={action.title} placeholder="Quest title"
              onChange={(e) => onChange({ ...action, title: e.target.value })} style={{ ...logicInputStyle, flex: 1, minWidth: 140 }} />
          </Fragment>
        )}
        {(action.kind === 'complete-quest' || action.kind === 'fail-quest') && (
          <input className="ss-input" value={action.questId} placeholder="quest-id"
            onChange={(e) => onChange({ ...action, questId: e.target.value })} style={{ ...logicInputStyle, flex: 1, minWidth: 120 }} />
        )}
        {canRemove && (
          <button className="ss-btn ss-btn--ghost" onClick={onRemove}
            style={{ padding: '4px 9px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7, color: 'var(--ss-danger)' }}>Remove</button>
        )}
      </div>
      {unsupported && (
        <div style={{ fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-warning)', paddingLeft: 8 }}>
          {LOGIC_ACTION_LABELS[action.kind]} is not run by the live game server yet, so this action will be
          skipped in a match until server support ships.
        </div>
      )}
    </div>
  );
}

// Shared across the three panels below (docs/editor-consolidation/
// panel-content-spec.md §5): which rule is open. `logic.rules` writes it (row
// click, "+ Rule"); `logic.rule-editor` only reads it.
export const useLogicSelectedRuleId = createTransientSelectionStore<string | null>(null);

// Guards the load-time validation/migration (dropping stored rules that
// aren't contract-shaped, toasting once) so it runs exactly once per
// project regardless of how many Logic panels are mounted - it used to be
// coupled to LogicRoute's own single hydration effect; now that the doc is
// shared (useSharedModeDocument), every panel reading it would otherwise
// re-run this the moment it mounts.
const logicMigratedProjects = new Set<string>();

/** Rules list + "+ Rule" (docs/editor-consolidation/panel-content-spec.md
 * §5, `logic.rules`). Owns the one-time load-time validation/migration for
 * the whole Logic mode-group, since it's the panel every layout preset
 * keeps docked. */
export function LogicRulesListPanel() {
  const { projectId } = useEditorHost();
  const notify = useToast();
  const { data: storedDoc, setData: saveDoc, loading: docLoading } = useSharedModeDocument<LogicDoc>('logic', LOGIC_DOC_FALLBACK);
  const [selId, setSelId] = useLogicSelectedRuleId.use();

  useEffect(() => {
    if (docLoading || logicMigratedProjects.has(projectId)) return;
    logicMigratedProjects.add(projectId);
    // Only contract-shaped rules hydrate; anything older falls back to the
    // starters rather than rendering a rule the server could not execute.
    const stored = storedDoc.rules ?? [];
    const valid = stored.filter((r) => typeof r?.when === 'string' && Array.isArray(r?.then));
    if (valid.length < stored.length) {
      const droppedCount = stored.length - valid.length;
      notify(
        `${droppedCount} logic rule${droppedCount > 1 ? 's' : ''} couldn't be loaded (unrecognised format) and will be permanently removed the next time Logic saves here. Avoid saving in this mode if you need to recover ${droppedCount > 1 ? 'them' : 'it'} some other way first.`,
        'error',
      );
    }
    const next = valid.length > 0 ? valid : LOGIC_RULES;
    if (valid.length !== stored.length) saveDoc({ version: LOGIC_DOCUMENT_VERSION, rules: next });
    setSelId((current) => current ?? next[0]?.id ?? null);
  }, [docLoading, storedDoc, notify, saveDoc, projectId, setSelId]);

  const rules = storedDoc.rules ?? [];

  function addRule() {
    const id = `rule-${Date.now().toString(36)}`;
    const next: AuthoredLogicRule = {
      id,
      name: 'New rule',
      when: 'round-start',
      then: [{ kind: 'announce', message: 'Round underway.' }],
      enabled: true,
    };
    saveDoc((doc) => ({ version: LOGIC_DOCUMENT_VERSION, rules: [...(doc.rules ?? []), next] }));
    setSelId(id);
  }

  return (
    <M2Panel side="left" width={252}>
      <M2Label action={<button className="ss-btn ss-btn--ghost" style={{ padding: '3px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }} onClick={addRule}>+ Rule</button>}>Rules</M2Label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' }}>
        {rules.map((r) => {
          // Per-rule compatibility warning (SF23.4): flag a rule that
          // carries an action the live realtime server does not run yet,
          // so an author sees it without opening every rule to check.
          const hasCompatWarning = r.then.some((a) => isActionKindUnsupported(a.kind));
          return (
            <M2ListRow
              key={r.id}
              active={selId === r.id}
              onClick={() => setSelId(r.id)}
              truncate={false}
              labelStyle={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px', borderRadius: 8, fontWeight: 500 }}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: r.enabled !== false ? 'var(--ss-success)' : 'var(--ss-text-3)', flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
              {hasCompatWarning && (
                <span title="Uses an action the live game server does not run yet" style={{ color: 'var(--ss-warning)', fontSize: 'var(--ss-fs-sm)', flexShrink: 0 }}>
                  ⚠
                </span>
              )}
            </M2ListRow>
          );
        })}
      </div>
      <div style={{ padding: '18px 16px 0', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
        Rules run on the server. No code needed; HollowScript can extend them later.
      </div>
    </M2Panel>
  );
}

/** Selected rule's When/If/Then editor (docs/editor-consolidation/
 * panel-content-spec.md §5, `logic.rule-editor`). */
export function LogicRuleEditorPanel() {
  const { project } = useEditorHost();
  const { data: storedDoc, setData: saveDoc } = useSharedModeDocument<LogicDoc>('logic', LOGIC_DOC_FALLBACK);
  const [selId] = useLogicSelectedRuleId.use();
  const { data: gameData } = useSharedModeDocument<GameDataDoc>('gamedata', GAMEDATA_DOC_FALLBACK);
  const itemIds = (gameData.items ?? []).map((i) => i.name).filter((n) => typeof n === 'string' && n !== '');
  const labelTriggers = (volumes: { kind: string; params?: Record<string, unknown> }[]) =>
    volumes.map((t, index) => {
      const site = t.params?.site;
      return typeof site === 'string' && site !== '' ? site : `${t.kind ?? 'trigger'}-${index}`;
    });
  const triggerIds = Array.from(
    new Set([...labelTriggers(project.map.triggers ?? []), ...labelTriggers(project.map.tilemap2d?.triggers ?? [])]),
  );

  const rules = storedDoc.rules ?? [];
  const rule = rules.find((r) => r.id === selId);
  const enabled = rule ? rule.enabled !== false : false;

  function patchRule(id: string, changes: Partial<AuthoredLogicRule>) {
    saveDoc((doc) => ({ version: LOGIC_DOCUMENT_VERSION, rules: (doc.rules ?? []).map((r) => (r.id === id ? { ...r, ...changes } : r)) }));
  }

  if (!rule) {
    return (
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ss-text-3)', fontSize: 'var(--ss-fs-sm)' }}>
        Select a rule to edit it.
      </div>
    );
  }

  return (
    <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '36px 32px 64px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22, gap: 16 }}>
          <input
            key={rule.id}
            defaultValue={rule.name}
            onBlur={(e) => patchRule(rule.id, { name: e.target.value.trim() || rule.name })}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            style={{
              fontFamily: 'var(--ss-display)', fontSize: 'var(--ss-fs-xl)', fontWeight: 500, letterSpacing: '-0.02em',
              background: 'transparent', border: 'none', color: 'var(--ss-text)', flex: 1, minWidth: 0,
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <span style={{ fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>{enabled ? 'Enabled' : 'Disabled'}</span>
            <button className={'ss-switch' + (enabled ? ' is-on' : '')} onClick={() => patchRule(rule.id, { enabled: !enabled })} aria-label="Rule enabled" />
          </div>
        </div>
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ position: 'absolute', left: 17, top: 28, bottom: 28, width: 1, background: 'var(--ss-border-soft)' }} />
          <div className="set-card" style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: 12, padding: '16px 18px', position: 'relative' }}>
            <LogicChip kind="when">When</LogicChip>
            <select
              className="ss-input"
              value={rule.when}
              onChange={(e) => patchRule(rule.id, { when: e.target.value as LogicEventKind })}
              style={{ appearance: 'none', padding: '8px 12px', fontSize: 'var(--ss-fs-sm)', borderRadius: 9, flex: 1, minWidth: 120 }}
            >
              {LOGIC_EVENT_KINDS.map((k) => (
                <option key={k} value={k}>{LOGIC_EVENT_LABELS[k]}</option>
              ))}
            </select>
          </div>
          <div className="set-card" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 18px', position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <LogicChip kind="if">If</LogicChip>
              {(rule.if ?? []).length === 0 && <span style={{ fontSize: 'var(--ss-fs-sm)', color: 'var(--ss-text-3)' }}>Always (no conditions)</span>}
              <button className="ss-btn ss-btn--ghost" style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}
                onClick={() => patchRule(rule.id, {
                  if: [...(rule.if ?? []), { fact: 'round', op: 'eq', value: 1 } as LogicCondition],
                })}>+ Condition</button>
            </div>
            {(rule.if ?? []).map((c, i) => (
              <ConditionRow
                key={i}
                condition={c}
                itemIds={itemIds}
                triggerIds={triggerIds}
                onChange={(next) => patchRule(rule.id, { if: (rule.if ?? []).map((x, j) => (j === i ? next : x)) })}
                onRemove={() => patchRule(rule.id, { if: (rule.if ?? []).filter((_, j) => j !== i) })}
              />
            ))}
          </div>
          <div className="set-card" style={{ padding: '16px 18px', gap: 12, position: 'relative', display: 'flex', flexDirection: 'column' }}>
            {rule.then.map((a, i) => (
              <ActionRow
                key={i}
                action={a}
                itemIds={itemIds}
                first={i === 0}
                canRemove={rule.then.length > 1}
                onChange={(next) => patchRule(rule.id, { then: rule.then.map((x, j) => (j === i ? next : x)) })}
                onRemove={() => patchRule(rule.id, { then: rule.then.filter((_, j) => j !== i) })}
              />
            ))}
            <button className="ss-btn ss-btn--ghost" style={{ alignSelf: 'flex-start', padding: '4px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}
              onClick={() => patchRule(rule.id, { then: [...rule.then, defaultAction('announce', itemIds)] })}>+ Action</button>
          </div>
        </div>
        <p style={{ marginTop: 22, fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>
          Rules save as a versioned document the game server executes. Items come from Game Data; zones come from triggers placed in Edit mode.
        </p>
      </div>
    </div>
  );
}

/** Static Events/Condition facts/Actions reference (docs/editor-
 * consolidation/panel-content-spec.md §5, `logic.reference`) - the simplest
 * panel in the whole decomposition: module constants only, no store, no
 * shared state, no dispose. */
export function LogicReferencePanel() {
  return (
    <M2Panel side="right" width={240}>
      <M2Label>Library</M2Label>
      {/* The live contract: exactly what the game server executes today.
          Actions lists SERVER_SUPPORTED_ACTION_KINDS, not the full
          LOGIC_ACTION_KINDS - a kind gated behind a feature flag (like
          'play-cutscene', SF23.4) isn't run by the server yet, so it
          doesn't belong in a list promising "executes today". */}
      {([
        ['Events', LOGIC_EVENT_KINDS.map((k) => LOGIC_EVENT_LABELS[k])],
        ['Condition facts', LOGIC_FACTS.map((f) => LOGIC_FACT_LABELS[f])],
        ['Actions', SERVER_SUPPORTED_ACTION_KINDS.map((a) => LOGIC_ACTION_LABELS[a])],
      ] as [string, string[]][]).map(([group, items]) => (
        <div key={group} style={{ padding: '0 16px', marginBottom: 14 }}>
          <div style={{ fontSize: 'var(--ss-fs-xs)', fontWeight: 600, color: 'var(--ss-text-2)', marginBottom: 7 }}>{group}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {items.map((it) => (
              <div key={it} style={{ fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', padding: '5px 10px', border: '1px solid var(--ss-border-soft)', borderRadius: 8 }}>{it}</div>
            ))}
          </div>
        </div>
      ))}
      <div style={{ padding: '0 16px', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.5 }}>
        Everything listed runs on the game server today; the library grows with the runtime.
      </div>
    </M2Panel>
  );
}

/* ── HUD Builder ──────────────────────────────────────────────────────── */
export interface HudWidget {
  id: string;
  label: string;
  pos: CSSProperties;
  w: number;
  h: number;
}

export const HUD_WIDGETS: HudWidget[] = [
  { id: 'health', label: 'Health bar', pos: { left: '4%', bottom: '6%' }, w: 150, h: 16 },
  { id: 'ammo', label: 'Resource counter', pos: { right: '4%', bottom: '6%' }, w: 70, h: 26 },
  { id: 'timer', label: 'Timer', pos: { left: '50%', top: '4%', transform: 'translateX(-50%)' }, w: 70, h: 24 },
  { id: 'minimap', label: 'Minimap', pos: { left: '3%', top: '4%' }, w: 90, h: 90 },
  { id: 'feed', label: 'Event feed', pos: { right: '3%', top: '5%' }, w: 130, h: 54 },
  { id: 'hotbar', label: 'Inventory hotbar', pos: { left: '50%', bottom: '5%', transform: 'translateX(-50%)' }, w: 168, h: 34 },
  { id: 'dialogue', label: 'Dialogue box', pos: { left: '50%', bottom: '16%', transform: 'translateX(-50%)' }, w: 250, h: 52 },
];

/** Live preview values for widgets that have a real source: elapsed time,
 * the player normalised into the map bounds, placed markers, runtime
 * trigger enter/exit events, and (once a `ClientLogicRuntime` is running)
 * the `health`/`ammo` Logic variables. Widgets without live data keep
 * design visuals. */
export interface HudLiveState {
  elapsedMs: number;
  /** Player position normalised 0..1 into the map bounds; null until the
   * first movement tick arrives. */
  player: [number, number] | null;
  markers: [number, number][];
  feed: string[];
  /** The `health`/`ammo` Logic variables, sampled from the running
   * `ClientLogicRuntime`. Undefined when no runtime is attached (e.g. the
   * HUD Builder's own static preview, which has nothing to sample) - the
   * HUD widget falls back to its design sample value in that case, not 0. */
  health?: number;
  ammo?: number;
  /** The match's outcome once `LogicEvaluator.outcome()` resolves (a
   * win-game/lose-game Logic action fired), else null - sampled from the
   * running `ClientLogicRuntime` on the same poll as health/ammo. Drives
   * `HudOverlayView`'s own result panel; the feed's own 'You win'/'{team}
   * wins' line (see `useHudPlayState`'s `onLogicEffect`) is the separate,
   * already-existing text-log entry, not this. */
  outcome?: { result: 'win' | 'lose'; team?: string } | null;
}

/** The headline text for a resolved match outcome - the same win/lose/team
 * phrasing the HUD feed's own `onLogicEffect` formatting already uses (see
 * `useHudPlayState` in screens-editor.tsx), shared here so the feed line
 * and the win/lose result panel never disagree. */
export function formatMatchOutcome(outcome: { result: 'win' | 'lose'; team?: string }): string {
  if (outcome.result === 'win') return outcome.team ? `${outcome.team} wins` : 'You win';
  return outcome.team ? `${outcome.team} loses` : 'You lose';
}

function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export interface HudWidgetMockProps {
  id: string;
  /** Game Data item names backing the hotbar (first five shown). */
  items?: string[];
  /** Live playtest values; absent in the HUD Builder, present in Preview. */
  live?: HudLiveState;
  /** The first authored Game Data dialogue node, shown as-is - not
   * event-triggered or branched yet (see Game Data's own "Logic and the HUD
   * don't read dialogue state yet" copy, which this narrows: the HUD now
   * reads the authored line, only branching/triggering remain unwired).
   * Absent (no dialogue authored) keeps the design sample. */
  dialogueLine?: { speaker: string; text: string };
}

export function HudWidgetMock({ id, items, live, dialogueLine }: HudWidgetMockProps) {
  if (id === 'health') {
    const pct = Math.max(0, Math.min(100, live?.health ?? 72));
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flex: 1, height: 7, borderRadius: 999, background: 'rgba(255,255,255,0.12)' }}>
          <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, background: '#9ed8a8' }} />
        </div>
        <span style={{ fontSize: 'var(--ss-fs-3xs)', fontWeight: 600, color: 'var(--ss-text-2)' }}>{Math.round(pct)}</span>
      </div>
    );
  }
  if (id === 'ammo') {
    const value = live?.ammo ?? 24;
    return <span style={{ fontFamily: 'var(--ss-display)', fontSize: 'var(--ss-fs-lg)', color: 'var(--ss-text)' }}>{value}{live?.ammo === undefined ? <span style={{ fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)' }}> / 90</span> : null}</span>;
  }
  if (id === 'timer') {
    return (
      <span style={{ fontFamily: 'var(--ss-display)', fontSize: 15, color: 'var(--ss-text)' }}>
        {live ? formatElapsed(live.elapsedMs) : '1:38'}
      </span>
    );
  }
  if (id === 'minimap') {
    return (
      <div style={{ width: '100%', height: '100%', borderRadius: 8, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.04)', position: 'relative', overflow: 'hidden' }}>
        {live ? (
          <Fragment>
            {live.markers.map(([u, v], i) => (
              <span key={i} style={{ position: 'absolute', left: `${u * 100}%`, top: `${v * 100}%`, width: 4, height: 4, marginLeft: -2, marginTop: -2, borderRadius: '50%', background: '#e8806c' }} />
            ))}
            {live.player && (
              <span style={{ position: 'absolute', left: `${live.player[0] * 100}%`, top: `${live.player[1] * 100}%`, width: 5, height: 5, marginLeft: -2.5, marginTop: -2.5, borderRadius: '50%', background: 'var(--ss-accent)' }} />
            )}
          </Fragment>
        ) : (
          <Fragment>
            <span style={{ position: 'absolute', left: '46%', top: '52%', width: 5, height: 5, borderRadius: '50%', background: 'var(--ss-accent)' }} />
            <span style={{ position: 'absolute', left: '64%', top: '30%', width: 4, height: 4, borderRadius: '50%', background: '#e8806c' }} />
          </Fragment>
        )}
      </div>
    );
  }
  if (id === 'feed') {
    if (live) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 8.5, color: 'var(--ss-text-3)', textAlign: 'right' }}>
          {live.feed.slice(-2).map((line, i) => (
            <span key={i} style={{ color: 'var(--ss-text-2)' }}>{line}</span>
          ))}
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 8.5, color: 'var(--ss-text-3)', textAlign: 'right' }}>
        <span><strong style={{ color: 'var(--ss-text-2)' }}>voxel_vandal</strong> found relic_key</span>
        <span><strong style={{ color: 'var(--ss-text-2)' }}>you</strong> completed the gate</span>
      </div>
    );
  }
  if (id === 'hotbar') {
    const slots = (items ?? []).slice(0, 5);
    return (
      <div style={{ display: 'flex', gap: 4 }}>
        {[0, 1, 2, 3, 4].map((n) => (
          <span key={n} title={slots[n] ?? ''} style={{ width: 24, height: 24, borderRadius: 5, border: '1px solid ' + (n === 1 ? 'var(--ss-accent)' : 'rgba(255,255,255,0.14)'), background: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 6.5, color: 'var(--ss-text-3)', overflow: 'hidden', textAlign: 'center', lineHeight: 1.1 }}>
            {slots[n] ? slots[n]!.slice(0, 6) : n + 1}
          </span>
        ))}
      </div>
    );
  }
  if (id === 'dialogue') {
    const line = dialogueLine ?? { speaker: 'KEEPER', text: 'The lighthouse remembers everyone who climbs it.' };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 8.5, color: 'var(--ss-text-2)', textAlign: 'left', width: '100%' }}>
        <strong style={{ fontSize: 8, color: 'var(--ss-accent)' }}>{line.speaker.toUpperCase()}</strong>
        <span style={{ color: 'var(--ss-text-3)' }}>{line.text}</span>
      </div>
    );
  }
  return null;
}

export function AnchorGrid({ value, onChange }: { value: number; onChange: (i: number) => void }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, width: 86 }}>
      {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
        <button key={i} onClick={() => onChange(i)} aria-label={'Anchor ' + i} style={{
          aspectRatio: '1', borderRadius: 5,
          border: '1px solid ' + (value === i ? 'var(--ss-accent-border)' : 'var(--ss-border-soft)'),
          background: value === i ? 'var(--ss-accent)' : 'rgba(255,255,255,0.03)', cursor: 'pointer',
        }} />
      ))}
    </div>
  );
}

/** Per-widget presentation tuning, persisted with the HUD document. */
export interface HudWidgetTuning {
  /** Percent; 100 is the authored size. */
  scale: number;
  /** Percent opacity. */
  opacity: number;
}

export const HUD_TUNING_DEFAULT: HudWidgetTuning = { scale: 100, opacity: 90 };

/** The 'hud' mode document: the enabled widget set, the anchor choice, and
 * per-widget scale/opacity tuning. */
export type HudDoc = {
  active: string[];
  anchor: number;
  widgets?: Record<string, HudWidgetTuning>;
};

export const HUD_DOC_FALLBACK: HudDoc = { active: ['health', 'ammo', 'timer', 'minimap'], anchor: 6, widgets: {} };

/** A widget's persisted tuning with defaults applied. */
export function hudTuning(doc: Pick<HudDoc, 'widgets'>, id: string): HudWidgetTuning {
  return { ...HUD_TUNING_DEFAULT, ...(doc.widgets?.[id] ?? {}) };
}

// Widgets with no live source in the HUD Builder itself, which has no
// running game to sample - labelled as samples there. Health, ammo and
// dialogue do have a real source now (a running ClientLogicRuntime's
// `health`/`ammo` variables, and the first authored Game Data dialogue
// line), but only in Preview/Play, which is what actually runs the game;
// the builder screen stays sample-only for these regardless. Timer,
// minimap and the event feed have always sourced from Preview's own
// position/timer tracking, independent of Logic.
const HUD_SAMPLE_WIDGETS = ['health', 'ammo', 'dialogue'];

// Shared across the three panels below (docs/editor-consolidation/
// panel-content-spec.md §6): which widget is selected. Both hud.widgets and
// hud.stage can set it (a three-way share, unlike Logic's two-way one);
// hud.properties only reads it.
export const useHudSelectedWidgetId = createTransientSelectionStore<string | null>(null);

/** Widget on/off toggle list (docs/editor-consolidation/panel-content-
 * spec.md §6, `hud.widgets`). */
export function HudWidgetListPanel() {
  const { data: storedDoc, setData: saveDoc, loading: docLoading } = useSharedModeDocument<HudDoc>('hud', HUD_DOC_FALLBACK);
  const [sel, setSel] = useHudSelectedWidgetId.use();
  const active = storedDoc.active;

  // Default to the first active widget once the document has loaded, same
  // as the pre-decomposition HudRoute did on its own mount - otherwise a
  // freshly opened HUD Builder shows hud.properties' "select a widget on
  // the stage" empty state even though a real widget is already active and
  // visible on the stage.
  useEffect(() => {
    if (docLoading || sel !== null) return;
    setSel(active[0] ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- default-select-once; re-running on every `active`/`sel` change would fight a deliberate "select a widget on the stage" empty state left after e.g. toggling the last active widget off.
  }, [docLoading]);

  return (
    <M2Panel side="left" width={232}>
      <M2Label>Widgets</M2Label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '0 12px' }}>
        {HUD_WIDGETS.map((w) => {
          const on = active.includes(w.id);
          return (
            <M2ListRow
              key={w.id}
              active={on}
              truncate={false}
              onClick={() => {
                if (on) {
                  saveDoc((doc) => ({ ...doc, active: doc.active.filter((x) => x !== w.id) }));
                  if (sel === w.id) setSel(null);
                } else {
                  saveDoc((doc) => ({ ...doc, active: [...doc.active, w.id] }));
                  setSel(w.id);
                }
              }}
              labelStyle={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 9,
                padding: '9px 12px', borderRadius: 9,
                border: '1px solid ' + (on ? 'var(--ss-accent-border)' : 'var(--ss-border-soft)'),
                fontWeight: 500,
              }}
            >
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
                {w.label}
                {HUD_SAMPLE_WIDGETS.includes(w.id) && (
                  <span style={{ fontSize: 'var(--ss-fs-3xs)', color: 'var(--ss-text-3)', letterSpacing: '0.06em' }}>sample</span>
                )}
              </span>
              <span style={{ fontSize: 'var(--ss-fs-2xs)', color: on ? 'var(--ss-accent)' : 'var(--ss-text-3)' }}>{on ? 'ON' : 'OFF'}</span>
            </M2ListRow>
          );
        })}
      </div>
      <div style={{ padding: '18px 16px 0', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
        Click a widget on the stage to select it. The HUD scales with resolution automatically.
      </div>
    </M2Panel>
  );
}

/** The 16:9 preview stage (docs/editor-consolidation/panel-content-spec.md
 * §6, `hud.stage`). */
export function HudStagePanel() {
  const { data: storedDoc } = useSharedModeDocument<HudDoc>('hud', HUD_DOC_FALLBACK);
  const { data: gameData } = useSharedModeDocument<GameDataDoc>('gamedata', GAMEDATA_DOC_FALLBACK);
  const hotbarItems = (gameData.items ?? []).map((i) => i.name);
  const [sel, setSel] = useHudSelectedWidgetId.use();
  const active = storedDoc.active;
  const tunings = storedDoc.widgets ?? {};

  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'radial-gradient(ellipse 70% 60% at 50% 40%, var(--ss-surface), var(--ss-bg-2) 80%)', padding: 28, position: 'relative' }}>
      <div style={{ position: 'relative', width: '100%', maxWidth: 820, aspectRatio: '16 / 9', borderRadius: 12, border: '1px solid var(--ss-border)', background: 'linear-gradient(180deg, #101013, #0a0a0c)', overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.5)' }}>
        <span style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: 14, height: 14, opacity: 0.6 }}>
          <span style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1.5, background: 'var(--ss-text-2)', transform: 'translateX(-50%)' }} />
          <span style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 1.5, background: 'var(--ss-text-2)', transform: 'translateY(-50%)' }} />
        </span>
        {HUD_WIDGETS.filter((w) => active.includes(w.id)).map((w) => {
          const t = hudTuning({ widgets: tunings }, w.id);
          return (
            <button key={w.id} onClick={() => setSel(w.id)} style={{
              position: 'absolute', ...w.pos, width: w.w, height: w.h,
              transform: `${(w.pos.transform as string | undefined) ?? ''} scale(${t.scale / 100})`.trim(),
              opacity: t.opacity / 100,
              background: 'rgba(7,7,9,0.5)', backdropFilter: 'blur(4px)',
              border: '1px solid ' + (sel === w.id ? 'var(--ss-accent)' : 'rgba(255,255,255,0.07)'),
              borderRadius: 8, padding: '4px 8px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: sel === w.id ? '0 0 14px var(--ss-accent-soft)' : 'none',
            }}>
              <HudWidgetMock id={w.id} items={hotbarItems} />
            </button>
          );
        })}
      </div>
      <span style={{
        position: 'absolute', top: 14, left: 14, background: 'var(--ss-glass)', backdropFilter: 'blur(8px)',
        border: '1px solid var(--ss-border-soft)', borderRadius: 999, padding: '7px 14px',
        fontWeight: 500, fontSize: 'var(--ss-fs-2xs)', letterSpacing: '0.05em', color: 'var(--ss-text-2)', pointerEvents: 'none',
      }}>match_hud · 1920×1080 reference</span>
    </div>
  );
}

/** Anchor + Scale/Opacity properties for the selected widget (docs/editor-
 * consolidation/panel-content-spec.md §6, `hud.properties`). */
export function HudPropertiesPanel() {
  const { data: storedDoc, setData: saveDoc } = useSharedModeDocument<HudDoc>('hud', HUD_DOC_FALLBACK);
  const [sel] = useHudSelectedWidgetId.use();
  const selW = HUD_WIDGETS.find((w) => w.id === sel);
  const tunings = storedDoc.widgets ?? {};

  function patchTuning(id: string, changes: Partial<HudWidgetTuning>) {
    saveDoc((doc) => ({ ...doc, widgets: { ...doc.widgets, [id]: { ...HUD_TUNING_DEFAULT, ...doc.widgets?.[id], ...changes } } }));
  }

  return (
    <M2Panel side="right" width={248}>
      <M2Label>Properties</M2Label>
      {selW ? (
        <Fragment>
          <div style={{ padding: '0 16px', fontSize: 'var(--ss-fs-sm)', fontWeight: 600, marginBottom: 12 }}>{selW.label}</div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '0 16px', marginBottom: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <span className="ss-field-label">Anchor</span>
              <AnchorGrid value={storedDoc.anchor} onChange={(v) => saveDoc((doc) => ({ ...doc, anchor: v }))} />
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span className="ss-field-label">Offset</span>
              <input className="ss-input" placeholder="Anchor preset" disabled title="Pixel offsets arrive with HUD anchor scaling" style={{ padding: '7px 10px', fontSize: 'var(--ss-fs-sm)', borderRadius: 8, opacity: 0.45 }} />
            </div>
          </div>
          <EdSlider label="Scale" value={hudTuning({ widgets: tunings }, selW.id).scale}
            onChange={(v) => patchTuning(selW.id, { scale: v })} />
          <EdSlider label="Opacity" value={hudTuning({ widgets: tunings }, selW.id).opacity}
            onChange={(v) => patchTuning(selW.id, { opacity: v })} />
        </Fragment>
      ) : (
        <div style={{ padding: '0 16px', fontSize: 'var(--ss-fs-sm)', color: 'var(--ss-text-3)' }}>Select a widget on the stage.</div>
      )}
    </M2Panel>
  );
}

/* ── Audio ────────────────────────────────────────────────────────────── */
export function Waveform({ seed = 3, colour = 'var(--ss-accent)' }: { seed?: number; colour?: string }) {
  const bars = Array.from({ length: 64 }, (_, i) => {
    const t = Math.abs(Math.sin(i * seed * 0.7) * 0.7 + Math.sin(i * 0.31 + seed) * 0.3);
    return 8 + t * 52;
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, height: 68 }}>
      {bars.map((h, i) => (
        <span key={i} style={{ flex: 1, height: h, borderRadius: 999, background: i < 22 ? colour : 'rgba(255,255,255,0.12)' }} />
      ))}
    </div>
  );
}

export interface AudioSound {
  name: string;
  kind: string;
  len: string;
  seed: number;
}

/** A placed audio zone: a looping sound at (x, z) fading out by `radius`.
 * Units follow the project: metres in 3D, tiles in 2D. `sound` is an uploaded
 * audio asset id. */
export interface AudioZone {
  id: string;
  name: string;
  sound: string;
  x: number;
  z: number;
  radius: number;
  falloff: 'smooth' | 'linear';
}

export interface AudioMixerLevels {
  master: number;
  music: number;
  sfx: number;
  ambience: number;
}

// Design sample sounds: shown only as empty-state content until real audio is
// uploaded. They carry no playable source.
export const AUDIO_SOUNDS: AudioSound[] = [
  { name: 'stinger_win', kind: 'SFX', len: '0:02', seed: 5 },
  { name: 'door_blast_open', kind: 'SFX', len: '0:01', seed: 9 },
  { name: 'footstep_concrete', kind: 'SFX', len: '0:01', seed: 13 },
  { name: 'theme_main', kind: 'Music', len: '2:44', seed: 2 },
  { name: 'amb_wind_low', kind: 'Ambience', len: '1:12', seed: 7 },
];

export const AUDIO_MIXER_DEFAULT: AudioMixerLevels = { master: 80, music: 55, sfx: 75, ambience: 40 };

/** The 'audio' mode document: placed audio zones plus the mixer levels. */
export type AudioDoc = {
  zones: AudioZone[];
  mixer: AudioMixerLevels;
};

export const AUDIO_DOC_FALLBACK: AudioDoc = { zones: [], mixer: AUDIO_MIXER_DEFAULT };

/**
 * Normalise any stored audio document (including the retired pre-runtime
 * shape, whose zones carried display strings like radius '6 m') to the
 * current AudioDoc.
 */
export function migrateAudioDoc(raw: unknown): AudioDoc {
  const doc = (raw ?? {}) as { zones?: unknown; mixer?: Partial<AudioMixerLevels> };
  const zones: AudioZone[] = Array.isArray(doc.zones)
    ? doc.zones.flatMap((z, i) => {
        const zone = z as Partial<AudioZone> & { radius?: number | string; falloff?: string };
        if (typeof zone.name !== 'string') return [];
        const radius = typeof zone.radius === 'number' ? zone.radius : parseFloat(String(zone.radius ?? ''));
        return [{
          id: typeof zone.id === 'string' ? zone.id : `${zone.name}-${i}`,
          name: zone.name,
          sound: typeof zone.sound === 'string' ? zone.sound : '',
          x: typeof zone.x === 'number' ? zone.x : 0,
          z: typeof zone.z === 'number' ? zone.z : 0,
          radius: Number.isFinite(radius) && radius > 0 ? radius : 6,
          falloff: String(zone.falloff ?? '').toLowerCase() === 'linear' ? 'linear' : 'smooth',
        }];
      })
    : [];
  const mixer = { ...AUDIO_MIXER_DEFAULT, ...(typeof doc.mixer === 'object' && doc.mixer ? doc.mixer : {}) };
  return { zones, mixer };
}

function formatDuration(seconds: number | undefined): string {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function seedFromName(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) % 97;
  return (hash % 13) + 1;
}

// Shared across the four panels below (docs/editor-consolidation/
// panel-content-spec.md §7): the selected library asset. Set by
// audio.library (row click, successful import); read by audio.preview.
export const useAudioSelectedAssetId = createTransientSelectionStore<string | null>(null);

/** Asset browser + Import (docs/editor-consolidation/panel-content-spec.md
 * §7, `audio.library`). Reads project.assets directly - it never touches
 * the audio document. */
export function AudioLibraryPanel() {
  const { projectId } = useEditorHost();
  const { project, reload } = useProject();
  const notify = useToast();
  const audioAssets = project.assets.filter((a) => a.kind === 'audio');
  const [sel, setSel] = useAudioSelectedAssetId.use();
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const importInputRef = useRef<HTMLInputElement>(null);

  async function importAudio(file: File) {
    setImporting(true);
    setImportError('');
    try {
      const asset = await uploadAudioAsset(projectId, file);
      await reload();
      setSel(asset.id);
      // durationSeconds comes back 0 both for a genuinely instantaneous clip
      // and for one the browser couldn't decode at all; the latter is far
      // more likely for a real upload, so flag it instead of staying silent.
      if (asset.durationSeconds === 0) {
        notify(`"${asset.name}" uploaded, but its duration couldn't be read - double-check it plays correctly.`, 'error');
      }
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed.');
    } finally {
      setImporting(false);
    }
  }

  return (
    <M2Panel side="left" width={252}>
      <M2Label action={
        <Fragment>
          <input ref={importInputRef} type="file" accept=".mp3,.ogg,.wav" style={{ display: 'none' }}
            onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; if (file) void importAudio(file); }} />
          <button className="ss-btn ss-btn--ghost" style={{ padding: '3px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}
            disabled={importing} onClick={() => importInputRef.current?.click()}>{importing ? 'Importing…' : '+ Import'}</button>
        </Fragment>
      }>Library</M2Label>
      {importError && <div style={{ padding: '0 16px 8px', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-danger)', lineHeight: 1.5 }}>{importError}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' }}>
        {audioAssets.map((a) => (
          <button key={a.id} onClick={() => setSel(a.id)} style={{
            display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px', borderRadius: 8, border: 'none', textAlign: 'left',
            background: (sel ?? audioAssets[0]?.id) === a.id ? 'var(--ss-accent-soft)' : 'transparent',
            color: (sel ?? audioAssets[0]?.id) === a.id ? 'var(--ss-accent)' : 'var(--ss-text-2)', fontSize: 'var(--ss-fs-sm)', fontWeight: 450,
          }}>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
            <span style={{ fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', flexShrink: 0 }}>{formatDuration(a.durationSeconds)}</span>
          </button>
        ))}
        {audioAssets.length === 0 && (
          <Fragment>
            {AUDIO_SOUNDS.map((x) => (
              <div key={x.name} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px', borderRadius: 8, color: 'var(--ss-text-3)', fontSize: 'var(--ss-fs-sm)', opacity: 0.7 }}>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.name}</span>
                <span style={{ fontSize: 'var(--ss-fs-2xs)', flexShrink: 0 }}>sample</span>
              </div>
            ))}
            <div style={{ padding: '8px 8px 0', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.5 }}>
              Samples preview the layout. Import audio to build your library.
            </div>
          </Fragment>
        )}
      </div>
    </M2Panel>
  );
}

/** Master/Music/SFX/Ambience sliders (docs/editor-consolidation/
 * panel-content-spec.md §7, `audio.mixer`). */
export function AudioMixerPanel() {
  const { data: storedDoc, setData: saveDoc } = useSharedModeDocument<AudioDoc>('audio', AUDIO_DOC_FALLBACK);
  const mixer = migrateAudioDoc(storedDoc).mixer;

  function patchMixer(changes: Partial<AudioMixerLevels>) {
    saveDoc((doc) => {
      const migrated = migrateAudioDoc(doc);
      return { ...migrated, mixer: { ...migrated.mixer, ...changes } };
    });
  }

  return (
    <M2Panel side="left" width={252}>
      <M2Label>Mixer</M2Label>
      <EdSlider label="Master" value={mixer.master} onChange={(v) => patchMixer({ master: v })} />
      <EdSlider label="Music" value={mixer.music} onChange={(v) => patchMixer({ music: v })} />
      <EdSlider label="SFX" value={mixer.sfx} onChange={(v) => patchMixer({ sfx: v })} />
      <EdSlider label="Ambience" value={mixer.ambience} onChange={(v) => patchMixer({ ambience: v })} />
    </M2Panel>
  );
}

/** Waveform + Play, and the one live AudioEngine instance for this mode-
 * group (docs/editor-consolidation/panel-content-spec.md §7, `audio.preview`
 * - see its "native resource ownership" note: this panel's dispose is the
 * only place engine.dispose() is called, since it already needs the shared
 * mixer value to drive its own setMix effect regardless of which panel
 * changed it). */
export function AudioPreviewPanel() {
  const { project } = useProject();
  const notify = useToast();
  const audioAssets = project.assets.filter((a) => a.kind === 'audio');
  const [sel] = useAudioSelectedAssetId.use();
  const { data: storedDoc } = useSharedModeDocument<AudioDoc>('audio', AUDIO_DOC_FALLBACK);
  const mixer = migrateAudioDoc(storedDoc).mixer;
  const selected = audioAssets.find((a) => a.id === sel) ?? audioAssets[0];
  const engineRef = useRef<AudioEngine | null>(null);

  useEffect(() => {
    const engine = new AudioEngine();
    engineRef.current = engine;
    return () => {
      engine.dispose();
      engineRef.current = null;
    };
  }, []);

  // The editor preview plays through the same engine mix the runtime uses.
  useEffect(() => {
    engineRef.current?.setMix({
      masterVolume: mixer.master / 100,
      musicVolume: mixer.music / 100,
      sfxVolume: mixer.sfx / 100,
      ambienceVolume: mixer.ambience / 100,
    });
  }, [mixer]);

  function playSelected() {
    if (!selected) return;
    void engineRef.current?.playOneShot(selected.downloadUrl, 'sfx').then((played) => {
      if (!played) notify(`Couldn't play "${selected.name}" - the file may be corrupt or unreachable.`, 'error');
    });
  }

  return (
    <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '36px 32px 64px' }}>
        {selected ? (
          <div className="set-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button className="ss-btn ss-btn--primary" style={{ width: 40, height: 40, padding: 0, borderRadius: '50%', justifyContent: 'center' }} aria-label="Play" onClick={playSelected}>
                  <svg width="12" height="13.2" viewBox="0 0 10 11" fill="none" aria-hidden="true"><path d="M1.5 1.3v8.4l7.2-4.2-7.2-4.2z" fill="currentColor" /></svg>
                </button>
                <div>
                  <h3 style={{ fontSize: 15.5, fontWeight: 600 }}>{selected.name}</h3>
                  <span className="hint">Uploaded audio · {formatDuration(selected.durationSeconds)}</span>
                </div>
              </div>
              <span style={{ fontWeight: 600, fontSize: 'var(--ss-fs-3xs)', letterSpacing: '0.1em', color: 'var(--ss-text-3)', border: '1px solid var(--ss-border-soft)', borderRadius: 999, padding: '3px 10px', textTransform: 'uppercase' }}>Audio</span>
            </div>
            <Waveform seed={seedFromName(selected.name)} />
          </div>
        ) : (
          <div className="set-card">
            <h3 style={{ fontSize: 15.5, fontWeight: 600 }}>No audio yet</h3>
            <span className="hint">Import an .mp3, .ogg or .wav to preview it here and assign it to zones.</span>
          </div>
        )}
      </div>
    </div>
  );
}

/** Audio zones list + fields (docs/editor-consolidation/panel-content-
 * spec.md §7, `audio.zones`). */
export function AudioZonesPanel() {
  const { project } = useProject();
  const audioAssets = project.assets.filter((a) => a.kind === 'audio');
  const { data: storedDoc, setData: saveDoc } = useSharedModeDocument<AudioDoc>('audio', AUDIO_DOC_FALLBACK);
  const zones = migrateAudioDoc(storedDoc).zones;

  function patchZone(id: string, changes: Partial<AudioZone>) {
    saveDoc((doc) => {
      const migrated = migrateAudioDoc(doc);
      return { ...migrated, zones: migrated.zones.map((z) => (z.id === id ? { ...z, ...changes } : z)) };
    });
  }
  function addZone() {
    const id = `zone-${Date.now().toString(36)}`;
    saveDoc((doc) => {
      const migrated = migrateAudioDoc(doc);
      return {
        ...migrated,
        zones: [...migrated.zones, {
          id, name: `zone_${migrated.zones.length + 1}`, sound: audioAssets[0]?.id ?? '',
          x: 0, z: 0, radius: 6, falloff: 'smooth' as const,
        }],
      };
    });
  }
  function removeZone(id: string) {
    saveDoc((doc) => {
      const migrated = migrateAudioDoc(doc);
      return { ...migrated, zones: migrated.zones.filter((z) => z.id !== id) };
    });
  }

  const zoneNumStyle: CSSProperties = { width: 52, padding: '6px 8px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 };

  return (
    <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '36px 32px 64px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 6px' }}>
          <h3 style={{ fontSize: 15, fontWeight: 600 }}>Audio zones</h3>
          <button className="ss-btn ss-btn--ghost ss-btn--sm" onClick={addZone}>+ Zone</button>
        </div>
        {zones.map((z) => (
          <div key={z.id} className="ss-row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ss-accent)', flexShrink: 0 }} />
            <input className="ss-input" value={z.name} onChange={(e) => patchZone(z.id, { name: e.target.value })}
              style={{ flex: 1, minWidth: 110, padding: '6px 8px', fontSize: 'var(--ss-fs-sm)', borderRadius: 7, fontWeight: 500 }} />
            <select className="ss-input" value={z.sound} onChange={(e) => patchZone(z.id, { sound: e.target.value })}
              style={{ appearance: 'none', width: 130, padding: '6px 8px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}>
              <option value="">No sound</option>
              {audioAssets.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <input className="ss-input" type="number" title="X" value={z.x} onChange={(e) => patchZone(z.id, { x: +e.target.value || 0 })} style={zoneNumStyle} />
            <input className="ss-input" type="number" title="Z" value={z.z} onChange={(e) => patchZone(z.id, { z: +e.target.value || 0 })} style={zoneNumStyle} />
            <input className="ss-input" type="number" title="Radius" min={1} value={z.radius} onChange={(e) => patchZone(z.id, { radius: Math.max(1, +e.target.value || 1) })} style={zoneNumStyle} />
            <select className="ss-input" value={z.falloff} onChange={(e) => patchZone(z.id, { falloff: e.target.value === 'linear' ? 'linear' : 'smooth' })}
              style={{ appearance: 'none', width: 86, padding: '6px 8px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }}>
              <option value="smooth">Smooth</option>
              <option value="linear">Linear</option>
            </select>
            <button className="ss-btn ss-btn--ghost" style={{ padding: '4px 9px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7, color: 'var(--ss-danger)' }}
              onClick={() => removeZone(z.id)}>Remove</button>
          </div>
        ))}
        {zones.length === 0 && (
          <div className="ss-row" style={{ color: 'var(--ss-text-3)', fontSize: 'var(--ss-fs-sm)' }}>No zones yet. + Zone places one at the map origin.</div>
        )}
        <p style={{ marginTop: 18, fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>
          Zones loop their sound in Preview and fade by distance from (x, z): metres in 3D, tiles in 2D.
        </p>
      </div>
    </div>
  );
}
